import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeftCard } from '@/shared/images/icons/ArrowLeftCard';

export type ScrollCarouselProps = {
    children: ReactNode;
    ariaLabel: string;
    className?: string;
};

export const ScrollCarousel = ({ children, ariaLabel, className = '' }: ScrollCarouselProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const list = scrollRef.current;

        if (!list) return;

        setCanScrollLeft(list.scrollLeft > 0);
        setCanScrollRight(list.scrollLeft + list.clientWidth < list.scrollWidth - 1);
    }, []);

    useEffect(() => {
        const frame = requestAnimationFrame(updateScrollState);
        window.addEventListener('resize', updateScrollState);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', updateScrollState);
        };
    }, [children, updateScrollState]);

    const scroll = (direction: 'left' | 'right') => {
        const list = scrollRef.current;

        if (!list) return;

        list.scrollBy({
            left: direction === 'left' ? -(list.clientWidth - 40) : list.clientWidth - 40,
            behavior: 'smooth',
        });
    };

    return (
        <div className="relative">
            <div
                ref={scrollRef}
                onScroll={updateScrollState}
                role="region"
                aria-label={ariaLabel}
                className={`custom-scroll flex max-w-full snap-x gap-5 overflow-x-auto pb-2 sm:grid sm:grid-cols-2 sm:overflow-visible sm:pb-0 ${className}`}
            >
                {children}
            </div>
            {canScrollLeft && (
                <span className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white-normal via-white-normal/5 to-transparent dark:from-dark-3 dark:via-dark-3/5 sm:hidden" />
            )}
            {canScrollRight && (
                <span className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white-normal via-white-normal/5 to-transparent dark:from-dark-3 dark:via-dark-3/5 sm:hidden" />
            )}
            {canScrollLeft && (
                <button
                    type="button"
                    aria-label={`Show previous ${ariaLabel}`}
                    onClick={() => scroll('left')}
                    className="absolute -left-4 top-1/2 z-10 -translate-y-1/2 rounded-full sm:hidden outline-none border-none bg-transparent cursor-pointer"
                >
                    <ArrowLeftCard />
                </button>
            )}
            {canScrollRight && (
                <button
                    type="button"
                    aria-label={`Show more ${ariaLabel}`}
                    onClick={() => scroll('right')}
                    className="absolute -right-4 top-1/2 z-10 -translate-y-1/2 rounded-full sm:hidden outline-none border-none bg-transparent cursor-pointer"
                >
                    <ArrowLeftCard className="rotate-180" />
                </button>
            )}
        </div>
    );
};
